import { defineComponent as _defineComponent } from 'vue'
import { attachmentLoadingAnimationColorTailwindClass } from '@@/bits/attachments'
import device from '@@/bits/device'
import overflowBorderRadiusTransformFixStyle from '@@/bits/overflow_border_radius_transform_fix'
import { postColorValues } from '@@/bits/post_color'
import BluePurpleLanterns from '@@/images/blue_purple_lanterns.png'
import type { PostColor } from '@@/types'
import BaseImage from '@@/vuecomponents/base_image.vue'
import { processedUrl, proxiedUrl } from '@padlet/vivaldi-client'
import { sample } from 'lodash-es'
import { computed, ref, withDefaults } from 'vue'


export default /*#__PURE__*/_defineComponent({
  __name: 'ImageThumbnail',
  props: {
    src: null,
    originalImageWidth: null,
    originalImageHeight: null,
    width: { default: undefined },
    height: { default: undefined },
    resizeStrategy: { default: 'crop' },
    aspectRatio: { default: undefined },
    alt: { default: undefined },
    title: { default: '' },
    lazyLoading: { type: Boolean, default: true },
    proxyImage: { type: Boolean, default: false },
    processImage: { type: Boolean, default: false },
    placeholderColor: { default: 'transparent' },
    placeholderColorValue: { default: undefined },
    fallbackToPlaceholder: { type: Boolean, default: false },
    placeholderAnimationVariant: { default: undefined },
    dataTestid: { default: undefined },
    flipDirection: { default: undefined },
    objectPosition: { default: undefined },
    fetchPriority: { default: 'auto' }
  },
  emits: ["load", "error"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'load'): void
  (e: 'error', error: Error): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as { src: string, originalImageWidth: number, originalImageHeight: number, width: number, height: number, resizeStrategy: 'crop' | 'stretch' | 'fit', aspectRatio: number, alt: string, title: string, lazyLoading: boolean, proxyImage: boolean, processImage: boolean, placeholderColor: string, placeholderColorValue: string, fallbackToPlaceholder: boolean, placeholderAnimationVariant: 'dark' | 'light', dataTestid: string, flipDirection: 'horizontal' | 'vertical', objectPosition: string, fetchPriority: 'auto' | 'high' | 'low' };





const loaded = ref(false)
const error = ref(false)

const errorPlaceholderImage = BluePurpleLanterns

const onLoad = (): void => {
  loaded.value = true
  emit('load')
}

const onError = (e: Error): void => {
  error.value = true
  emit('error', e)
}

const overflowBorderRadiusStyle = computed(() => overflowBorderRadiusTransformFixStyle(device.safari))

const bgColorClass = computed(() => {
  if (props.placeholderColorValue) return ''

  if (loaded.value) return ''

  if (
    props.placeholderAnimationVariant &&
    postColorValues.includes(props.placeholderColor === 'default' ? null : (props.placeholderColor as PostColor))
  ) {
    return attachmentLoadingAnimationColorTailwindClass({
      postColor: props.placeholderColor as PostColor,
      isLightColorScheme: props.placeholderAnimationVariant === 'light',
    })
  }

  const colorClassMap = {
    red: 'bg-bgi-red',
    blue: 'bg-bgi-blue',
    green: 'bg-bgi-green',
    yellow: 'bg-bgi-yellow',
    purple: 'bg-bgi-purple',
    pink: 'bg-bgi-pink',
    cyan: 'bg-bgi-cyan',
    orange: 'bg-bgi-orange',
    grey: 'bg-highway-200',
  }

  if (props.placeholderColor === 'random') {
    return sample(Object.values(colorClassMap)) ?? ''
  } else {
    return colorClassMap[props.placeholderColor] || ''
  }
})

const url = computed(() => {
  if (props.processImage) {
    const processingOptions: { width: number; height?: number; ar?: number } = { width: props.width }
    if (props.height) processingOptions.height = props.height
    if (props.resizeStrategy === 'stretch' && props.aspectRatio) processingOptions.ar = props.aspectRatio
    return processedUrl(props.src, processingOptions)
  }
  return props.proxyImage ? proxiedUrl(props.src) : props.src
})

const originalImageAspectRatio = computed(() => props.originalImageWidth / props.originalImageHeight)

const dimensions = computed(() => {
  // Width is fixed except when resizeStrategy === 'fit' (CASE 3)
  let cropFrameWidth = props.width
  let unit: 'px' | '%' = 'px'

  // These are decided based on the resizing strategy.
  let cropFrameHeight: number
  let cropFrameAspectRatio: number
  let imageElementHeight: number
  let imageElementWidth: number

  // CASE 1: Crop to fit aspect ratio
  if (props.resizeStrategy === 'crop' && props.aspectRatio) {
    // Try to fit as much of the image in the crop frame as possible.
    cropFrameAspectRatio = props.aspectRatio

    // All cropFrameHeight calculation in this method should use Math.ceil.
    // Reason:
    // Browsers can display HTML elements with fractional height.
    // In Vivaldi, when calculating height using width and aspect ratio, we use
    // Math.ceil (see [1] and [2]). So the resulting image height will never be
    // fractional. But the <div> in this component can have fractional height.
    // -> By using Math.ceil, we ensure the <div> will have the same height as
    // the image.
    //
    // [1]: https://github.com/padlet/mozart/blob/21a2867e9c2abf5e71db0bb6c3eaa6b86b6cd904/services/vivaldi/src/vips/fill.js#L30
    // [2]: https://github.com/padlet/mozart/blob/21a2867e9c2abf5e71db0bb6c3eaa6b86b6cd904/services/vivaldi/src/vips/limit_fill.js#L39
    cropFrameHeight = Math.ceil(cropFrameWidth / cropFrameAspectRatio)
    const originalHasWiderAspectRatio = originalImageAspectRatio.value > props.aspectRatio
    if (originalHasWiderAspectRatio) {
      imageElementHeight = cropFrameHeight
      imageElementWidth = originalImageAspectRatio.value * cropFrameHeight
    } else {
      imageElementWidth = cropFrameWidth
      imageElementHeight = imageElementWidth / originalImageAspectRatio.value
    }

    // CASE 2: Stretch to fit aspect ratio
  } else if (props.resizeStrategy === 'stretch' && props.aspectRatio) {
    // Image element and crop frame both have the same dimensions.
    cropFrameAspectRatio = props.aspectRatio
    cropFrameHeight = Math.ceil(cropFrameWidth / cropFrameAspectRatio)
    imageElementWidth = cropFrameWidth
    imageElementHeight = cropFrameHeight

    // CASE 3: Fit parent's dimensions
  } else if (props.resizeStrategy === 'fit') {
    // Fit the image to its parent element regardless of aspect ratio.
    cropFrameAspectRatio = originalImageAspectRatio.value
    cropFrameWidth = 100
    cropFrameHeight = 100
    imageElementWidth = 100
    imageElementHeight = 100
    unit = '%'

    // CASE 4: Scale, keeping original aspect ratio
  } else {
    // Maintain the same ratio as the original, but scale down proportionately if need be.
    cropFrameAspectRatio = originalImageAspectRatio.value
    cropFrameHeight = Math.ceil(cropFrameWidth / cropFrameAspectRatio)
    imageElementWidth = cropFrameWidth
    imageElementHeight = cropFrameHeight
  }

  return {
    cropFrameWidth,
    cropFrameWidthString: `${cropFrameWidth}${unit}`,
    cropFrameHeight,
    cropFrameHeightString: `${cropFrameHeight}${unit}`,
    cropFrameAspectRatio,
    imageElementWidth,
    imageElementWidthString: `${imageElementWidth}${unit}`,
    imageElementHeight,
    imageElementHeightString: `${imageElementHeight}${unit}`,
  }
})

return { __sfc: true,props, emit, loaded, error, errorPlaceholderImage, onLoad, onError, overflowBorderRadiusStyle, bgColorClass, url, originalImageAspectRatio, dimensions, BaseImage }
}

})